import { auth } from '../auth/firebase.js';
import i18n from '../i18n/index.js';

// 後端有些 route 只回機器代碼、沒有 message（如 not_member / unauthorized）。
// 直接把代碼丟到畫面上就是 0805 東訊回報的那類問題 → 這裡兜一句人話，
// 代碼收進括號裡給客服/工程對照。有對應 i18n key 就用專屬說明。
function humaniseCode(code) {
    if (!code) return '';
    const specific = i18n.t(`common:error.code.${code}`, { defaultValue: '' });
    if (specific) return specific;
    return i18n.t('common:error.unknownWithCode', {
        code,
        defaultValue: `操作沒有完成（代碼：${code}）`,
    });
}

// 後端整個不通（502/504）或 route 沒回 JSON 時，連機器代碼都沒有。
// 舊的 fallback 是 `${method} ${path} → ${status}` → 客戶名單載入失敗時畫面直接
// 噴出 `GET /api/ivr/customers/list?hostId=...&limit=800 → 502`（0811 回報）。
// 內部路徑不該給客戶看，這裡改成依 HTTP status 兜人話，原始那行收進 err.detail + console。
const HTTP_FALLBACK_KEY = {
    401: 'error.http.401',
    403: 'error.http.403',
    404: 'error.http.404',
    408: 'error.http.timeout',
    429: 'error.http.429',
    504: 'error.http.timeout',
};
function humaniseStatus(status) {
    const key = HTTP_FALLBACK_KEY[status] || (status >= 500 ? 'error.http.5xx' : 'error.http.generic');
    return i18n.t(`common:${key}`, {
        status,
        defaultValue: `操作沒有完成（HTTP ${status}）`,
    });
}

/**
 * fetch wrapper that auto-attaches Firebase ID token + JSON handling.
 *
 * 後端 /api/ivr/admin/* 跟 /api/ivr/* 的 auth：
 *   - prod 應該是 Firebase ID token + email 白名單（M7 後端要加 middleware）
 *   - 過渡期是 Bearer RELAY_SHARED_SECRET（admin-console server.js proxy 時注入）
 *
 * 前端只送 Firebase token；server.js 的 proxy middleware 替換成 RELAY_SHARED_SECRET。
 */
export async function api(method, path, opts = {}) {
    const headers = {
        Accept: 'application/json',
        ...(opts.headers || {}),
    };
    if (opts.body && !(opts.body instanceof FormData)) {
        headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }
    // 等 Firebase auth hydrate,避免 SPA 開頁就點操作時 auth.currentUser 還 null →
    // fetch 不帶 token → admin-console proxy 回 401 → silent fail(例如 PreviewCallModal 拿不到 testDialerSetup → 模擬通話漏出 {{}})
    if (typeof auth.authStateReady === 'function') {
        await auth.authStateReady();
    }
    const user = auth.currentUser;
    if (user) {
        try {
            const idToken = await user.getIdToken();
            headers['Authorization'] = `Bearer ${idToken}`;
        } catch (e) {
            console.warn('[api] failed to get id token', e);
        }
    }
    const body =
        opts.body && !(opts.body instanceof FormData)
            ? JSON.stringify(opts.body)
            : opts.body;

    const res = await fetch(path, { method, headers, body, credentials: 'same-origin' });
    const text = await res.text();
    let parsed;
    try {
        parsed = text ? JSON.parse(text) : null;
    } catch {
        parsed = text;
    }
    if (!res.ok) {
        // 🔴 順序很重要：`message` 是給人看的說明，`error` 是機器代碼。
        //   原本 error 排前面 → 後端明明回了「AA 智慧總機角色已啟用 3/3 個，達到付費數量上限…」，
        //   使用者看到的卻是 `role_limit_exceeded`（0805 東訊回報；通訊錄上限同理）。
        //   代碼仍留在 err.code / err.body 給程式判斷，只是不再當成畫面文案。
        const human = typeof parsed?.message === 'string' ? parsed.message.trim() : '';
        const code = typeof parsed?.error === 'string' ? parsed.error.trim() : '';
        // 後端有些 route 把人話寫在 error 欄位（如通訊錄上限）→ 看起來像句子就直接用。
        const codeLooksHuman = code && (code.length > 40 || /[\s一-鿿]/.test(code));
        const err = new Error(human || (codeLooksHuman ? code : '') || humaniseCode(code) || humaniseStatus(res.status));
        err.status = res.status;
        err.code = parsed?.code || code || null;
        err.body = parsed;
        err.detail = `${method} ${path} → ${res.status}`;   // 給工程/客服對照，不進畫面
        console.warn('[api]', err.detail, parsed || '');
        throw err;
    }
    return parsed;
}

export const get = (p, h) => api('GET', p, { headers: h });
export const post = (p, body, h) => api('POST', p, { body, headers: h });
export const put = (p, body, h) => api('PUT', p, { body, headers: h });
export const del = (p, h) => api('DELETE', p, { headers: h });

/**
 * 帶 Firebase token 的檔案下載（blob）。
 *
 * ⚠️ 為什麼不能用 `<a href download>`：純瀏覽器導航不會帶 Authorization header，
 * admin-console proxy 的 authMiddleware 一看沒 token 直接 401 → 下載失敗。
 * 所以走跟 api() 同一套 token 流程的 fetch，拿 blob 後再觸發下載。
 */
export async function download(path, headers = {}) {
    const h = { ...headers };
    if (typeof auth.authStateReady === 'function') {
        await auth.authStateReady();
    }
    const user = auth.currentUser;
    if (user) {
        try {
            h['Authorization'] = `Bearer ${await user.getIdToken()}`;
        } catch (e) {
            console.warn('[api] download failed to get id token', e);
        }
    }
    const res = await fetch(path, { method: 'GET', headers: h, credentials: 'same-origin' });
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        // 後端回的純文字可能是 HTML error page / stack trace → 一樣不進畫面，只留人話。
        const looksHuman = text && text.length <= 200 && !/[<{]/.test(text);
        const err = new Error((looksHuman && text.trim()) || humaniseStatus(res.status));
        err.status = res.status;
        err.detail = `GET ${path} → ${res.status}`;
        console.warn('[api] download', err.detail, text ? text.slice(0, 300) : '');
        throw err;
    }
    const blob = await res.blob();
    // 檔名：優先用後端 Content-Disposition，fallback 從 path 取最後一段
    const cd = res.headers.get('Content-Disposition') || '';
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    const filename = (m && decodeURIComponent(m[1])) || path.split('/').pop().split('?')[0] || 'download';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
